/*
 * Versandkostenfrei-Fortschritt: Quick-Add und Animation.
 *
 * Zwei Aufgaben:
 *
 * 1. Quick-Add - die Kacheln im Band legen ihr Produkt per AJAX in den
 *    Warenkorb und lassen Drawer, Warenkorb-Seite und Header-Ring in einem
 *    Rutsch neu rendern.
 *
 * 2. Animation ueber Re-Renders hinweg. Dawns Section Rendering API ersetzt
 *    das Ring- bzw. Balken-Element komplett; ein frisch eingehaengtes Element
 *    startet keine CSS-Transition, der Fortschritt wuerde springen. Deshalb
 *    wird beim neuen Element zuerst der zuletzt gemerkte Wert gesetzt, ein
 *    Reflow erzwungen und dann der Zielwert - die Transition laeuft.
 */
(() => {
  /* Die Sections der Warenkorb-Seite muessen ueber ihre echte Section-ID aus
     data-id angefordert werden (z. B. template--123__cart-footer), nicht ueber
     den Dateinamen: nur so rendert Shopify sie mit den Bloecken aus
     templates/cart.json. Ueber den Dateinamen kaeme main-cart-footer ohne den
     Subtotal-Block und damit ohne .js-contents zurueck. Dawns cart.js macht es
     genauso. Die Section Rendering API nimmt hoechstens 5 Sections pro Aufruf. */
  const sectionTargets = () => {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const names = ['cart-icon-bubble'];

    if (document.querySelector('cart-drawer')) names.unshift('cart-drawer');

    const itemsId = items ? items.dataset.id || 'main-cart-items' : null;
    const footerId = footer ? footer.dataset.id || 'main-cart-footer' : null;

    if (itemsId) names.push(itemsId);
    if (footerId) names.push(footerId);

    return { names, itemsId, footerId };
  };

  /* ---------------------------------------------------------------- Animation */

  const last = { bag: null, bar: null };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const applyValue = (element, kind, value) => {
    if (kind === 'bag') {
      element.style.setProperty('--herder-cart-fill', String(value));
    } else {
      element.style.width = `${value}%`;
    }
  };

  const animateFrom = (element, kind, previous) => {
    element.dataset.herderAnimated = 'true';

    const target = Number(element.dataset.progress) || 0;
    last[kind] = target;

    // Erster Aufbau der Seite: es gibt keinen Vorher-Wert, der Stand steht
    // bereits korrekt im Inline-Style.
    if (previous === null || previous === target || prefersReducedMotion()) return;

    applyValue(element, kind, previous);
    // getBoundingClientRect statt offsetWidth: der Beutel ist ein SVG-Element
    // und hat gar kein offsetWidth - das Lesen wuerde keinen Reflow erzwingen
    // und die Transition liefe nie los.
    element.getBoundingClientRect();
    applyValue(element, kind, target);
  };

  const scan = () => {
    const bags = document.querySelectorAll('[data-herder-cart-bag]:not([data-herder-animated])');
    const bars = document.querySelectorAll('[data-herder-shipping-bar]:not([data-herder-animated])');

    // Vorher-Wert einmal je Art festhalten: Drawer und Warenkorb-Seite koennen
    // gleichzeitig im DOM stehen und sollen beide vom selben Stand loslaufen.
    const previousBag = last.bag;
    const previousBar = last.bar;

    bags.forEach((element) => animateFrom(element, 'bag', previousBag));
    bars.forEach((element) => animateFrom(element, 'bar', previousBar));
  };

  // Bewusst setTimeout statt requestAnimationFrame: rAF ruht in Hintergrund-
  // Tabs, der Stand wuerde dort erst beim Zurueckwechseln nachgezogen. Den
  // Zwischenzustand fuer die Transition erzwingt ohnehin der Reflow in
  // animateFrom, dafuer braucht es keinen Frame-Callback.
  let scheduled = false;
  const scheduleScan = () => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      scan();
    }, 0);
  };

  const observe = () => {
    const observer = new MutationObserver(scheduleScan);

    // Nur die Container beobachten, die Dawn tatsaechlich austauscht.
    ['#cart-icon-bubble', '#CartDrawer', '#main-cart-items', '#cart-notification-shipping'].forEach((selector) => {
      const node = document.querySelector(selector);
      if (node) observer.observe(node, { childList: true, subtree: true });
    });

    scan();
  };

  /* ---------------------------------------------------------------- Quick-Add */

  const parseSection = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const updateSection = (html, targetSelector, sourceSelector) => {
    const target = document.querySelector(targetSelector);
    const source = parseSection(html, sourceSelector);
    if (target && source) target.innerHTML = source.innerHTML;
  };

  const addVariant = async (button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.classList.contains('is-loading')) return;

    const band = button.closest('[data-herder-shipping]');
    const label = button.querySelector('span');
    const originalText = label ? label.textContent : '';
    const errorNode = band ? band.querySelector('[data-herder-shipping-error]') : null;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    if (label && band) label.textContent = band.dataset.loadingText || originalText;
    if (errorNode) errorNode.hidden = true;

    const targets = sectionTargets();

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: targets.names,
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(
          state.description || state.message || (band && band.dataset.errorText) || 'Fehler'
        );
      }

      const sections = state.sections || {};
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(state);
      } else {
        updateSection(sections['cart-icon-bubble'], '#cart-icon-bubble', '.shopify-section');
      }

      if (targets.itemsId) {
        updateSection(sections[targets.itemsId], '#main-cart-items .js-contents', '.js-contents');
      }
      if (targets.footerId) {
        updateSection(sections[targets.footerId], '#main-cart-footer .js-contents', '.js-contents');
      }

      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'herder-shipping-motivator',
          productVariantId: String(variantId),
          cartData: state,
        });
      }

      scheduleScan();
    } catch (requestError) {
      if (errorNode) {
        errorNode.textContent = requestError.message;
        errorNode.hidden = false;
      }
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
      if (label) label.textContent = originalText;
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-herder-quick-add]');
    if (button) addVariant(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
